import { useFetcher } from "@remix-run/react";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { refreshWorkflowToken } from "#app/utils/auth.client";
import { getJSON, removeKey, setJSON } from "#app/utils/storage.client";
import { useOptionalUser } from "#app/utils/user";

interface Organization {
  id: string;
  name: string;
  roles: string[];
  imageUrl?: string;
  imageBlob?: string;
  imageType?: string;
}

interface Team {
  id: string;
  name: string;
  prefix: string;
  roles: string[];
  imageUrl?: string;
  imageBlob?: string;
  imageType?: string;
}

interface OrgTeamContextType {
  currentOrg: Organization | null;
  currentTeam: Team | null;
  organizations: Organization[];
  teams: Team[];
  setCurrentOrg: (org: Organization | null) => void;
  setCurrentTeam: (team: Team | null) => void;
  loadOrganizations: () => Promise<void>;
  loadTeams: (orgId: string) => Promise<void>;
  clearCache: () => void;
  isLoading: boolean;
  loadOrgImages: () => Promise<void>;
  loadTeamImages: (orgId: string) => Promise<void>;
  orgImages: Record<
    string,
    { imageUrl?: string; imageBlob?: string; imageType?: string }
  >;
  teamImages: Record<
    string,
    { imageUrl?: string; imageBlob?: string; imageType?: string }
  >;
}

const OrgTeamContext = createContext<OrgTeamContextType | undefined>(undefined);

interface OrgTeamProviderProps {
  children: React.ReactNode;
}

export const ORG_TEAM_SELECTION_CHANGED_EVENT = "org-team:selection-changed";

/**
 * Module scope, not component scope.
 *
 * It was `const ttl30d = ...` inside the provider: a literal rebuilt on every
 * render, which five hooks then had to either list as a dependency or omit and
 * be warned about. A constant that cannot change does not belong in either.
 */
const TTL_30D_MS = 30 * 24 * 60 * 60 * 1000;

export const OrgTeamProvider: React.FC<OrgTeamProviderProps> = ({
  children,
}) => {
  const fetcher = useFetcher();
  const user = useOptionalUser();

  // Used to revalidate route data after org/team selection changes and the
  // session cookie has been updated by `/api/v1/org-team`.
  const pendingSelectionSyncRef = useRef(false);

  // When user selects an org that has teams, we defer API submit until they select a team.
  // Precedence: team > org > personal.
  const orgPendingApiSubmitRef = useRef(false);
  // Avoid submitting org (no-teams case) more than once per org.
  const lastSubmittedNoTeamsOrgIdRef = useRef<string | null>(null);
  // Avoid submitting org+team more than once per selection.
  const lastSubmittedOrgTeamKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pendingSelectionSyncRef.current) return;
    if (fetcher.state !== "idle") return;

    pendingSelectionSyncRef.current = false;
    if (typeof window !== "undefined") {
      // Refresh workflow token when org or team selection changes.
      void refreshWorkflowToken();
      // Allow cookie application to settle before listeners revalidate.
      setTimeout(() => {
        window.dispatchEvent(new Event(ORG_TEAM_SELECTION_CHANGED_EVENT));
      }, 0);
    }
  }, [fetcher.state]);

  const loadingStateRef = useRef({
    organizations: false,
    teams: false,
    lastOrgId: null as string | null,
  });

  const orgImagesLoadedRef = useRef(false);
  const orgImagesInFlightRef = useRef<Promise<void> | null>(null);
  /** Used so we only clear team/teams cache when switching orgs, not on full page reload (lastOrgId starts null). */
  const previousOrgIdForTeamsRef = useRef<string | null>(null);

  // State for organization and team images
  const [orgImages, setOrgImages] = useState<
    Record<
      string,
      { imageUrl?: string; imageBlob?: string; imageType?: string }
    >
  >({});
  const [teamImages, setTeamImages] = useState<
    Record<
      string,
      { imageUrl?: string; imageBlob?: string; imageType?: string }
    >
  >({});

  const storageNamespace = "org_team";
  /** When true, user chose Personal workspace; keep it across reloads (see loadOrganizations auto-pick). */
  const PREFER_PERSONAL_KEY = "preferPersonalWorkspace";

  // Helper function to safely access localStorage (only on client)
  const _getLocalStorageItem = useCallback((key: string) => {
    if (typeof window !== "undefined" && window.localStorage) {
      return localStorage.getItem(key);
    }
    return null;
  }, []);

  // Helper function to safely parse localStorage items
  const getParsedLocalStorageItem = useCallback(
    <T,>(key: string, fallback: T): T => {
      const item = getJSON<T>(key, { namespace: storageNamespace });
      return item ?? fallback;
    },
    [storageNamespace],
  );

  const [organizations, setOrganizations] = useState<Organization[]>(() => {
    // Initialize from localStorage if available (only on client)
    if (typeof window !== "undefined") {
      return getParsedLocalStorageItem<Organization[]>("organizations", []);
    }
    return [];
  });

  const [teams, setTeams] = useState<Team[]>(() => {
    // Initialize from localStorage if available (only on client)
    if (typeof window !== "undefined") {
      return getParsedLocalStorageItem<Team[]>("teams", []);
    }
    return [];
  });

  const [currentOrg, setCurrentOrgState] = useState<Organization | null>(() => {
    // Initialize from localStorage if available (only on client)
    if (typeof window !== "undefined") {
      if (
        getJSON<boolean>(PREFER_PERSONAL_KEY, { namespace: storageNamespace })
      ) {
        return null;
      }
      return getParsedLocalStorageItem<Organization | null>("currentOrg", null);
    }
    return null;
  });

  const [currentTeam, setCurrentTeamState] = useState<Team | null>(() => {
    // Initialize from localStorage if available (only on client)
    if (typeof window !== "undefined") {
      if (
        getJSON<boolean>(PREFER_PERSONAL_KEY, { namespace: storageNamespace })
      ) {
        return null;
      }
      return getParsedLocalStorageItem<Team | null>("currentTeam", null);
    }
    return null;
  });

  const [isLoading, setIsLoading] = useState(false);

  // Load organization images
  const loadOrgImages = useCallback(async () => {
    // Avoid repeated fetches across re-renders / navigations.
    if (orgImagesLoadedRef.current) return;

    // Cache-first (client-only).
    if (typeof window !== "undefined") {
      const cached = getJSON<
        Record<
          string,
          { imageUrl?: string; imageBlob?: string; imageType?: string }
        >
      >("orgImages", { namespace: storageNamespace });
      if (cached && Object.keys(cached).length > 0) {
        setOrgImages(cached);
        orgImagesLoadedRef.current = true;
        return;
      }
    }

    // In-flight dedupe (prevents multiple concurrent requests).
    if (orgImagesInFlightRef.current) {
      await orgImagesInFlightRef.current;
      return;
    }

    const p = (async () => {
      try {
        const response = await fetch("/api/v1/organizations/meta");
        if (!response.ok) return;

        const data = await response.json();
        const imageMap: Record<
          string,
          { imageUrl?: string; imageBlob?: string; imageType?: string }
        > = {};

        if (Array.isArray(data)) {
          for (const item of data) {
            if (item && typeof item === "object" && "organizationId" in item) {
              const orgMeta = item as any;
              imageMap[orgMeta.organizationId] = {
                imageUrl: orgMeta.imageUrl || undefined,
                imageBlob: orgMeta.imageBlob || undefined,
                imageType: orgMeta.imageType || undefined,
              };
            }
          }
        }

        setOrgImages(imageMap);
        if (typeof window !== "undefined") {
          setJSON("orgImages", imageMap, {
            namespace: storageNamespace,
            ttlMs: TTL_30D_MS,
          });
        }
        orgImagesLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load organization images:", error);
      }
    })();

    orgImagesInFlightRef.current = p;
    try {
      await p;
    } finally {
      orgImagesInFlightRef.current = null;
    }
  }, []);

  // Load team images for a specific organization
  const loadTeamImages = useCallback(async (orgId: string) => {
    try {
      const response = await fetch(
        `/api/v1/organizations/${orgId}/team-member-counts`,
      );
      if (response.ok) {
        const data: any = await response.json();
        const imageMap: Record<
          string,
          { imageUrl?: string; imageBlob?: string; imageType?: string }
        > = {};

        if (data.teamMetas && Array.isArray(data.teamMetas)) {
          for (const meta of data.teamMetas) {
            if (meta && typeof meta === "object" && "teamId" in meta) {
              imageMap[meta.teamId] = {
                imageUrl: meta.imageUrl || undefined,
                imageBlob: meta.imageBlob || undefined,
                imageType: meta.imageType || undefined,
              };
            }
          }
        }

        setTeamImages(imageMap);
      }
    } catch (error) {
      console.error("Failed to load team images:", error);
    }
  }, []);

  // Set current organization and sync with server immediately so loaders that
  // depend on org context (billing, quotas, dashboards) see the same scope as the UI.
  const setCurrentOrg = useCallback(
    (org: Organization | null) => {
      orgPendingApiSubmitRef.current = false;
      lastSubmittedOrgTeamKeyRef.current = null;
      lastSubmittedNoTeamsOrgIdRef.current = org?.id ?? null;
      setCurrentOrgState(org);
      // Always clear team locally when org changes (team > org precedence).
      setCurrentTeamState(null);

      const formData = new FormData();
      formData.append("intent", "setCurrentOrg");
      formData.append("org", org ? JSON.stringify(org) : "null");
      pendingSelectionSyncRef.current = true;
      fetcher.submit(formData, {
        method: "post",
        action: "/api/v1/org-team",
      });

      if (typeof window !== "undefined") {
        if (org) {
          removeKey(PREFER_PERSONAL_KEY, storageNamespace);
          setJSON("currentOrg", org, {
            namespace: storageNamespace,
            ttlMs: TTL_30D_MS,
          });
        } else {
          setJSON(PREFER_PERSONAL_KEY, true, {
            namespace: storageNamespace,
            ttlMs: TTL_30D_MS,
          });
          removeKey("currentOrg", storageNamespace);
        }
        removeKey("currentTeam", storageNamespace);
      }
    },
    [fetcher],
  );

  // Set current team (and sync with server).
  // If org was deferred (org has teams), submit org+team in one call; otherwise submit team only.
  const setCurrentTeam = useCallback(
    (team: Team | null) => {
      setCurrentTeamState(team);

      if (orgPendingApiSubmitRef.current && currentOrg && team) {
        // Org had teams; we deferred API. Now submit both org and team (precedence: team > org).
        orgPendingApiSubmitRef.current = false;
        lastSubmittedOrgTeamKeyRef.current = `${currentOrg.id}:${team.id}`;
        const formData = new FormData();
        formData.append("intent", "setCurrentOrgAndTeam");
        formData.append("org", JSON.stringify(currentOrg));
        formData.append("team", JSON.stringify(team));
        pendingSelectionSyncRef.current = true;
        fetcher.submit(formData, {
          method: "post",
          action: "/api/v1/org-team",
        });
      } else {
        const formData = new FormData();
        formData.append("intent", "setCurrentTeam");
        formData.append("team", team ? JSON.stringify(team) : "null");
        pendingSelectionSyncRef.current = true;
        fetcher.submit(formData, {
          method: "post",
          action: "/api/v1/org-team",
        });
      }

      if (typeof window !== "undefined") {
        if (team) {
          setJSON("currentTeam", team, {
            namespace: storageNamespace,
            ttlMs: TTL_30D_MS,
          });
        } else {
          removeKey("currentTeam", storageNamespace);
        }
      }
    },
    [fetcher, currentOrg],
  );

  const loadOrganizations = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (loadingStateRef.current.organizations) {
      return;
    }

    loadingStateRef.current.organizations = true;
    setIsLoading(true);

    try {
      const response = await fetch("/api/v1/organizations/user-organizations", {
        signal: AbortSignal.timeout(10_000),
      });

      if (response.ok) {
        const orgs = (await response.json()) as Organization[];
        setOrganizations(orgs);
        // Only update localStorage on client side
        if (typeof window !== "undefined") {
          setJSON("organizations", orgs, {
            namespace: storageNamespace,
            ttlMs: TTL_30D_MS,
          });
        }

        // Load organization images
        await loadOrgImages();

        // Set current org if none is set but we have organizations
        if (!currentOrg && orgs.length > 0) {
          let orgToSet: Organization | null = null;

          // Only access localStorage on client side
          if (typeof window !== "undefined") {
            const preferPersonal = getJSON<boolean>(PREFER_PERSONAL_KEY, {
              namespace: storageNamespace,
            });
            if (preferPersonal) {
              orgToSet = null;
            } else {
              const parsedOrg = getJSON<Organization>("currentOrg", {
                namespace: storageNamespace,
              });
              if (parsedOrg) {
                const orgExists = orgs.some((org) => org.id === parsedOrg.id);
                orgToSet = orgExists ? parsedOrg : orgs[0] || null;
              } else {
                orgToSet = orgs[0] || null;
              }
            }
          } else {
            // On server, just use the first org
            orgToSet = orgs[0] || null;
          }

          if (orgToSet) {
            setCurrentOrgState(orgToSet);
            // Only update localStorage on client side
            if (typeof window !== "undefined") {
              setJSON("currentOrg", orgToSet, {
                namespace: storageNamespace,
                ttlMs: TTL_30D_MS,
              });
              // Sync with server
              const formData = new FormData();
              formData.append("intent", "setCurrentOrg");
              formData.append("org", JSON.stringify(orgToSet));
              fetcher.submit(formData, {
                method: "post",
                action: "/api/v1/org-team",
              });
            }
          }
        }
      } else {
        console.error("Failed to load organizations, status:", response.status);
      }
    } catch (error) {
      if (error instanceof Error && error.name === "TimeoutError") {
        console.error("Organization loading timed out");
      } else {
        console.error("Failed to load organizations:", error);
      }
    } finally {
      setIsLoading(false);
      loadingStateRef.current.organizations = false;
    }
  }, [currentOrg, fetcher, loadOrgImages]);

  const loadTeams = useCallback(
    async (orgId: string) => {
      if (
        loadingStateRef.current.teams &&
        loadingStateRef.current.lastOrgId === orgId
      ) {
        return;
      }

      loadingStateRef.current.teams = true;
      loadingStateRef.current.lastOrgId = orgId;
      setIsLoading(true);

      try {
        const response = await fetch(`/api/v1/organizations/${orgId}/teams`, {
          signal: AbortSignal.timeout(10_000),
        });

        if (response.ok) {
          const contentType = response.headers.get("content-type") ?? "";
          if (!contentType.includes("application/json")) {
            // API returned HTML (e.g. login page or SPA fallback) instead of JSON
            console.error(
              "Failed to load teams: response was not JSON",
              response.status,
            );
            return;
          }
          const rawData: any = await response.json();
          const teamsList = (rawData.teams || []) as Team[];
          setTeams(teamsList);
          // Only update localStorage on client side
          if (typeof window !== "undefined") {
            setJSON("teams", teamsList, {
              namespace: storageNamespace,
              ttlMs: TTL_30D_MS,
            });
          }

          // Load team images
          await loadTeamImages(orgId);
        } else {
          console.error("Failed to load teams, status:", response.status);
        }
      } catch (error) {
        if (error instanceof Error && error.name === "TimeoutError") {
          console.error("Team loading timed out");
        } else {
          console.error("Failed to load teams:", error);
        }
      } finally {
        setIsLoading(false);
        loadingStateRef.current.teams = false;
      }
    },
    [loadTeamImages],
  );

  // Clear all cache data
  const loadOrganizationsRef = useRef(loadOrganizations);
  loadOrganizationsRef.current = loadOrganizations;

  const clearCache = useCallback(() => {
    // Clear localStorage items (only on client)
    if (typeof window !== "undefined") {
      removeKey("organizations", storageNamespace);
      removeKey("currentOrg", storageNamespace);
      removeKey(PREFER_PERSONAL_KEY, storageNamespace);
      removeKey("teams", storageNamespace);
      removeKey("currentTeam", storageNamespace);
      removeKey("orgImages", storageNamespace);
    }

    // Reset state
    setOrganizations([]);
    setTeams([]);
    setCurrentOrgState(null);
    setCurrentTeamState(null);
    setOrgImages({});
    setTeamImages({});
    orgImagesLoadedRef.current = false;
    orgImagesInFlightRef.current = null;

    // Reset loading state
    loadingStateRef.current.organizations = false;
    loadingStateRef.current.teams = false;
    loadingStateRef.current.lastOrgId = null;
    orgPendingApiSubmitRef.current = false;
    lastSubmittedNoTeamsOrgIdRef.current = null;
    lastSubmittedOrgTeamKeyRef.current = null;
    previousOrgIdForTeamsRef.current = null;
  }, []);

  // Load data from localStorage or server on mount
  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") {
      return;
    }
    // Skip fetching org/team data when unauthenticated to avoid redirect loops.
    // Guarded on `user?.id` rather than `user`, because that is what this effect
    // is keyed on -- reading the whole object would make the object's identity a
    // dependency for no gain.
    if (!user?.id) {
      return;
    }

    const initOrgTeamData = async () => {
      // Cache-first; fetch only if missing. Supports TTL-wrapped entries.
      const cachedOrganizations = getJSON<Organization[]>("organizations", {
        namespace: storageNamespace,
      });
      const cachedCurrentOrg = getJSON<Organization>("currentOrg", {
        namespace: storageNamespace,
      });
      const cachedTeams = getJSON<Team[]>("teams", {
        namespace: storageNamespace,
      });
      const cachedCurrentTeam = getJSON<Team>("currentTeam", {
        namespace: storageNamespace,
      });
      const preferPersonal = getJSON<boolean>(PREFER_PERSONAL_KEY, {
        namespace: storageNamespace,
      });

      if (cachedOrganizations && preferPersonal && !cachedCurrentOrg) {
        setOrganizations(cachedOrganizations);
        setCurrentOrgState(null);
        setCurrentTeamState(null);
        await loadOrgImages();
        return;
      }

      if (cachedOrganizations && cachedCurrentOrg) {
        setOrganizations(cachedOrganizations);
        setCurrentOrgState(cachedCurrentOrg);
        if (cachedTeams) setTeams(cachedTeams);
        if (cachedCurrentTeam) setCurrentTeamState(cachedCurrentTeam);

        await loadOrgImages();
        if (cachedCurrentOrg.id) {
          await loadTeamImages(cachedCurrentOrg.id);
        }

        if (!cachedTeams || cachedTeams.length === 0) {
          await loadTeams(cachedCurrentOrg.id);
        }
        return;
      }

      await loadOrganizationsRef.current();
    };

    void initOrgTeamData();
    // `loadOrganizations` is deliberately reached through a ref rather than
    // listed here. It depends on `currentOrg`, and calling it SETS
    // `currentOrg` -- so depending on its identity would re-run this
    // initialisation every time it succeeded. The ref calls the current one
    // while the effect stays keyed on the user it is initialising for.
  }, [loadOrgImages, loadTeamImages, loadTeams, user?.id]);

  // Listen for global refresh events from parts of the app that mutate org/team state
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      try {
        const ce = e as CustomEvent<{ orgId?: string }>;
        // Fetch latest orgs and teams for provided or current org without clearing selections
        void loadOrganizations().then(() => {
          if (ce.detail?.orgId) {
            void loadTeams(ce.detail.orgId);
          } else if (currentOrg?.id) {
            void loadTeams(currentOrg.id);
          }
        });
      } catch (err) {
        console.error("Failed to handle org-team:refresh event:", err);
      }
    };
    window.addEventListener("org-team:refresh", handler as EventListener);
    return () =>
      window.removeEventListener("org-team:refresh", handler as EventListener);
  }, [currentOrg?.id, loadOrganizations, loadTeams]);

  // Load teams when currentOrg changes
  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") {
      return;
    }

    if (currentOrg?.id) {
      const previousOrgId = previousOrgIdForTeamsRef.current;
      const orgChanged =
        previousOrgId !== null && previousOrgId !== currentOrg.id;

      // Only load teams if they haven't been loaded for this org yet
      if (loadingStateRef.current.lastOrgId !== currentOrg.id) {
        // Clear team only when switching orgs — not on first paint after reload (previousOrgId is null).
        if (orgChanged) {
          setTeams([]);
          setCurrentTeamState(null);
          removeKey("teams", storageNamespace);
          removeKey("currentTeam", storageNamespace);
        }

        void loadTeams(currentOrg.id);
      }
      previousOrgIdForTeamsRef.current = currentOrg.id;
    } else if (!currentOrg) {
      previousOrgIdForTeamsRef.current = null;
      // Clear teams when no org is selected
      setTeams([]);
      setCurrentTeamState(null);
      removeKey("teams", storageNamespace);
      removeKey("currentTeam", storageNamespace);
      loadingStateRef.current.lastOrgId = null;
    }
    // `loadTeams` used to be omitted here with a note about an infinite loop.
    // It is a useCallback with an empty dependency array, so it never changes
    // and cannot cause one. `currentOrg` is read as an object (`currentOrg.id`
    // inside a truthiness check), and a re-run with the same id is a no-op --
    // `loadingStateRef.current.lastOrgId` guards it.
  }, [currentOrg, loadTeams]);

  // After teams finish loading for selected org: if no teams, fire API for org only; if has teams, set pending or submit org+team if team already chosen (e.g. auto-selected).
  useEffect(() => {
    if (typeof window === "undefined" || !currentOrg?.id || isLoading) return;
    if (loadingStateRef.current.lastOrgId !== currentOrg.id) return;

    if (teams.length === 0) {
      if (lastSubmittedNoTeamsOrgIdRef.current === currentOrg.id) return;
      lastSubmittedNoTeamsOrgIdRef.current = currentOrg.id;
      lastSubmittedOrgTeamKeyRef.current = null;
      orgPendingApiSubmitRef.current = false;
      const formData = new FormData();
      formData.append("intent", "setCurrentOrg");
      formData.append("org", JSON.stringify(currentOrg));
      pendingSelectionSyncRef.current = true;
      fetcher.submit(formData, {
        method: "post",
        action: "/api/v1/org-team",
      });
    } else {
      lastSubmittedNoTeamsOrgIdRef.current = null;
      lastSubmittedOrgTeamKeyRef.current = null;
      // Org has teams; org context is already synced. Team selection will sync separately.
      orgPendingApiSubmitRef.current = false;
    }
  }, [currentOrg?.id, currentOrg, teams.length, isLoading, fetcher]);

  const value = React.useMemo(
    () => ({
      currentOrg,
      currentTeam,
      organizations,
      teams,
      setCurrentOrg,
      setCurrentTeam,
      loadOrganizations,
      loadTeams,
      clearCache,
      isLoading,
      loadOrgImages,
      loadTeamImages,
      orgImages,
      teamImages,
    }),
    [
      currentOrg,
      currentTeam,
      organizations,
      teams,
      isLoading,
      orgImages,
      teamImages,
      // The five loaders are part of the value this provider hands out, so the
      // value has to change when they do. Four are useCallbacks with empty
      // dependency arrays and never change; loadOrganizations changes with
      // `currentOrg`, which is already listed here, so nothing new is churned.
      clearCache,
      loadOrgImages,
      loadOrganizations,
      loadTeamImages,
      loadTeams,
      setCurrentOrg,
      setCurrentTeam,
    ],
  );

  return (
    <OrgTeamContext.Provider value={value}>{children}</OrgTeamContext.Provider>
  );
};

export function useOrgTeam() {
  const context = useContext(OrgTeamContext);
  if (context === undefined) {
    throw new Error("useOrgTeam must be used within an OrgTeamProvider");
  }
  return context;
}
